import axios from 'axios'
import { normalizeApiError } from '../utils/apiErrors'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Interceptor para agregar token JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Interceptor de respuesta para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    error.appError = normalizeApiError(error)

    if (error.response?.status === 401) {
      const detail = error.appError.detail || ''
      
      // Si es "Token inválido" o similar, limpiar localStorage y redirigir
      if (detail.toLowerCase().includes('token') || detail.toLowerCase().includes('invalido') || detail.toLowerCase().includes('no autorizado')) {
        console.warn('[API] Token inválido detectado, limpiando sesión...')
        localStorage.removeItem('access_token')
        localStorage.removeItem('user')
        
        // Esperar un momento y redirigir al login
        setTimeout(() => {
          window.location.href = '/login'
        }, 500)
      }
    }
    return Promise.reject(error)
  }
)

// Servicio de autenticación
export const authService = {
  login: (email, password, otp_code) => api.post('/auth/login', { email, password, otp_code }),
  logout: () => localStorage.removeItem('access_token'),
  getCurrentUser: () => api.get('/auth/me'),
  setup2FA: () => api.post('/auth/2fa/setup'),
  enable2FA: (otp_code) => api.post('/auth/2fa/enable', { otp_code }),
  updateProfile: (data) => api.put('/auth/profile', data)
}

// Servicio de empresas
export const empresaService = {
  listar: () => api.get('/empresas'),
  obtener: (id) => api.get(`/empresas/${id}`),
  crear: (data) => api.post('/empresas', data),
  actualizar: (id, data) => api.put(`/empresas/${id}`, data),
  eliminar: (id) => api.delete(`/empresas/${id}`),
  asignarPlantillaPdf: (empresaId, plantillaId) => (
    api.post(`/empresas/${empresaId}/plantilla-pdf/${plantillaId}`)
  ),
  // Credenciales SAT
  listarCredenciales: (empresaId) => api.get(`/empresas/${empresaId}/credenciales-sat`),
  guardarCredencial: (empresaId, data) => api.post(`/empresas/${empresaId}/credenciales-sat`, data),
  probarCredencial: (empresaId, credencialId) => api.post(`/empresas/${empresaId}/credenciales-sat/${credencialId}/probar`)
}

export const plantillaPdfService = {
  listar: () => api.get('/plantillas-pdf'),
  obtener: (id) => api.get(`/plantillas-pdf/${id}`),
  crear: (data) => api.post('/plantillas-pdf', data),
  actualizar: (id, data) => api.put(`/plantillas-pdf/${id}`, data),
  eliminar: (id) => api.delete(`/plantillas-pdf/${id}`),
  preview: (id) => api.get(`/plantillas-pdf/${id}/preview`, { responseType: 'arraybuffer' }),
}

// Servicio de facturas
export const facturaService = {
  listar: (params) => api.get('/facturas', { params }),
  listarEmpresasDisponibles: () => api.get('/facturas/empresas-disponibles'),
  listarClientesPorEmpresa: (empresaId) => api.get(`/facturas/clientes-por-empresa/${empresaId}`),
  crearCliente: (data) => api.post('/facturas/clientes', data),
  obtener: (id) => api.get(`/facturas/${id}`),
  crear: (data) => api.post('/facturas', data),
  crearYTimbrar: (data) => api.post('/facturas/crear-y-timbrar', data),  // ⚡ NUEVO: Crea y timbra en transacción atómica
  timbrar: (id, data = {}) => api.post(`/facturas/${id}/timbrar`, data),
  validar: (data) => api.post('/facturas/validar', data),
  descargarXML: (id) => api.get(`/facturas/${id}/xml`, { responseType: 'blob' }),
  descargarPDF: (id) => api.get(`/facturas/${id}/pdf`, { responseType: 'blob' }),
  generarXML: (id) => api.get(`/facturas/${id}/generar-xml`, { responseType: 'blob' }),
  descargarZIP: (id) => api.get(`/facturas/${id}/zip`, { responseType: 'blob' }),
  enviarCorreo: (id, destinatario = null) => api.post(`/facturas/${id}/enviar-correo`, { destinatario })
}

// Servicio de declaraciones
export const declaracionService = {
  listar: (params) => api.get('/declaraciones', { params }),
  obtener: (id) => api.get(`/declaraciones/${id}`),
  crear: (data) => api.post('/declaraciones', data),
  editar: (id, data) => api.put(`/declaraciones/${id}`, data),
  calcular: (data) => api.post('/declaraciones/calcular', data),
  presentar: (id) => api.post(`/declaraciones/${id}/presentar`)
}

// Servicio de líneas de captura
export const lineaCapturaService = {
  listar: (params) => api.get('/lineas-captura', { params }),
  obtener: (id) => api.get(`/lineas-captura/${id}`),
  generar: (data) => api.post('/lineas-captura/generar', data),
  confirmarPago: (id, datos) => api.post(`/lineas-captura/${id}/confirmar-pago`, datos)
}

export const pagosService = {
  listarCuentas: () => api.get('/pagos/cuentas'),
  generarCuentasDemo: () => api.post('/pagos/cuentas/demo'),
}

export const descargaMasivaService = {
  ejecutar: (data) => api.post('/descarga-masiva/ejecutar', data),
  mesAnterior: (empresaId) => api.post(`/descarga-masiva/mes-anterior/${empresaId}`),
  // Nuevos endpoints con polling
  iniciar: (data) => api.post('/descarga-masiva/iniciar', data),
  obtenerStatus: (jobId) => api.get(`/descarga-masiva/jobs/${jobId}/status`),
  obtenerResultado: (jobId) => api.get(`/descarga-masiva/jobs/${jobId}/resultado`)
}

// Servicio de reportes
export const reporteService = {
  impuestos: (params) => api.get('/reportes/impuestos', { params }),
  facturas: (params) => api.get('/reportes/facturas', { params }),
  declaraciones: (params) => api.get('/reportes/declaraciones', { params }),
  general: (params) => api.get('/reportes/general', { params }),
  facturasDetalle: (params) => api.get('/reportes/facturas-detalle', { params }),
  descargar: (tipo, params) => api.get(`/reportes/${tipo}/descargar`, { params, responseType: 'blob' })
}

// Servicio de dashboard analytics
export const dashboardService = {
  kpis: () => api.get('/dashboard/kpis'),
  tendenciaFacturacion: (meses = 6) => api.get('/dashboard/tendencia-facturacion', { params: { meses } }),
  distribucionGastos: () => api.get('/dashboard/distribucion-gastos'),
  cumplimientoMensual: (meses = 12) => api.get('/dashboard/cumplimiento-mensual', { params: { meses } }),
  proximosVencimientos: () => api.get('/dashboard/proximos-vencimientos')
}

// Servicio de catalogos SAT
export const catalogosService = {
  regimenesFiscales: () => api.get('/catalogos/regimenes-fiscales'),
  cfdi: () => api.get('/catalogos/cfdi'),
  productosServicios: (query = '', limit = 50) => api.get('/catalogos/productos-servicios', { params: { query, limit } }),
  obtenerTipoCambio: () => api.get('/catalogos/tipo-cambio'),
}

// Servicio de clientes
export const clienteService = {
  listar: () => api.get('/clientes'),
  listarPorEmpresa: (empresaId) => api.get(`/clientes/por-empresa/${empresaId}`),
  obtener: (id) => api.get(`/clientes/${id}`),
  crear: (data) => api.post('/clientes', data),
  actualizar: (id, data) => api.put(`/clientes/${id}`, data),
  eliminar: (id) => api.delete(`/clientes/${id}`)
}

// Servicio de suscripciones
export const suscripcionService = {
  listar: () => api.get('/suscripciones'),
  obtener: (id) => api.get(`/suscripciones/${id}`),
  crear: (data) => api.post('/suscripciones', data),
  actualizar: (id, data) => api.put(`/suscripciones/${id}`, data),
  eliminar: (id) => api.delete(`/suscripciones/${id}`),
  pendientes: () => api.get('/suscripciones/activas/pendientes'),
  procesar: (id) => api.post(`/suscripciones/${id}/procesar`)
}

// Servicio de salud SAT
export const saludSATService = {
  global: (meses = 3) => api.get('/salud-sat/global', { params: { meses } }),
  porEmpresa: (meses = 3) => api.get('/salud-sat/por-empresa', { params: { meses } })
}

// Servicio de consultas SAT
export const satService = {
  validarRFC: (rfc) => api.get(`/sat/validar-rfc/${rfc}`),
  buscarContribuyente: (rfc, guardar = false) => api.get(`/sat/buscar-contribuyente/${rfc}`, { params: { guardar } }),
  facturasRecibidas: (params) => api.get('/sat/facturas-recibidas', { params })
}

// Servicio de administración de usuarios (solo admin)
export const adminUsuariosService = {
  listar: () => api.get('/admin/usuarios'),
  obtener: (id) => api.get(`/admin/usuarios/${id}`),
  crear: (data) => api.post('/admin/usuarios', data),
  actualizar: (id, data) => api.put(`/admin/usuarios/${id}`, data),
  eliminar: (id) => api.delete(`/admin/usuarios/${id}`),
  asignarEmpresas: (id, empresaIds) => api.post(`/admin/usuarios/${id}/asignar-empresas`, { empresa_ids: empresaIds }),
  listarEmpresasAsignadas: (id) => api.get(`/admin/usuarios/${id}/empresas`),
  asignarTabs: (id, tabs) => api.post(`/admin/usuarios/${id}/asignar-tabs`, { tabs })
}

// Alias para compatibilidad
export const usuarioService = adminUsuariosService

// Servicio de proveedores
export const proveedorService = {
  listar: () => api.get('/proveedores'),
  obtener: (id) => api.get(`/proveedores/${id}`),
  crear: (data) => api.post('/proveedores', data),
  actualizar: (id, data) => api.put(`/proveedores/${id}`, data),
  eliminar: (id) => api.delete(`/proveedores/${id}`)
}

// Servicio de licencias
export const licenciaService = {
  listar: () => api.get('/licencias'),
  crear: (data) => api.post('/licencias', data),
  actualizar: (id, data) => api.put(`/licencias/${id}`, data),
  validar: (id, data) => api.post(`/licencias/${id}/validar`, data),
  eliminar: (id) => api.delete(`/licencias/${id}`)
}

// Servicio de DESC ARGA DE FACTURAS DEL SAT
export const descargaFacturasSATService = {
  // Iniciar descarga de facturas
  descargar: (data) => api.post('/facturas-sat/descargar', data),
  
  // Verificar estado de descarga
  verificar: (requestId, empresaId) => api.get(`/facturas-sat/verificar/${requestId}`, {
    params: { empresa_id: empresaId }
  }),
  
  // Obtener historial de descargas
  historial: (empresaId, skip = 0, limit = 10) => api.get('/facturas-sat/historial', {
    params: { empresa_id: empresaId, skip, limit }
  }),
  
  // Listar facturas descargadas
  listar: (empresaId, params = {}) => api.get('/facturas-sat/lista', {
    params: { empresa_id: empresaId, ...params }
  }),
  
  // Obtener detalles de una factura
  detalle: (uuid, empresaId) => api.get(`/facturas-sat/detalle/${uuid}`, {
    params: { empresa_id: empresaId }
  }),
  
  // Descargar XML de una factura
  descargarXML: (uuid, empresaId) => api.get(`/facturas-sat/descargar-xml/${uuid}`, {
    params: { empresa_id: empresaId },
    responseType: 'blob'
  }),
  
  // Sincronizar facturas con SAT
  sincronizar: (empresaId) => api.post(`/facturas-sat/sincronizar-sat/${empresaId}`)
}

// Servicio de billing y planes
export const billingService = {
  // Planes
  listarPlanes: () => api.get('/billing/planes'),
  obtenerPlan: (planId) => api.get(`/billing/planes/${planId}`),
  crearPlan: (data) => api.post('/billing/planes', data),
  actualizarPlan: (planId, data) => api.put(`/billing/planes/${planId}`, data),
  eliminarPlan: (planId) => api.delete(`/billing/planes/${planId}`),
  
  // Suscripciones del usuario
  obtenerMiSuscripcion: () => api.get('/billing/suscripcion/me'),
  crearOCambiarSuscripcion: (data) => api.post('/billing/suscripcion', data),
  obtenerLimites: () => api.get('/billing/limites/me'),
  obtenerCapacidades: () => api.get('/billing/capacidades/me'),
  obtenerMisLimites: () => api.get('/billing/limites/me'),
  obtenerMisCapacidades: () => api.get('/billing/capacidades/me'),
  
  // Admin: obtener suscripciones de otros usuarios
  obtenerSuscripcionUsuario: (usuarioId) => api.get(`/billing/admin/suscripcion/${usuarioId}`),
  listarSuscripcionesEmpresas: () => api.get('/billing/suscripciones/empresas'),
  obtenerEmpresasSuscripcion: (suscripcionId) => api.get(`/billing/admin/suscripcion/${suscripcionId}/empresas`),
  
  // Asignar plan a usuario (admin)
  asignarPlanAUsuario: (usuarioId, planId) => api.post('/billing/admin/asignar-plan', {
    usuario_id: usuarioId,
    plan_id: planId
  }),
  
  // Checkout y pagos
  iniciarCheckout: (data) => api.post('/billing/checkout', data),
  generarCheckoutLink: ({ monto, concepto }) => api.post('/billing/checkout-link', null, { params: { monto, concepto } }),
  confirmarMercadoPago: (data) => api.post('/billing/mercadopago/confirm', data),
  
  // Webhooks y estado
  enviarWebhook: (evento, token = '') => api.post('/billing/webhook', evento, { headers: token ? { 'x-webhook-token': token } : {} }),
  obtenerEstadoProveedor: () => api.get('/billing/provider-status')
}

export default api
